import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PriorityBadge } from "@/components/PriorityBadge";
import { KnowledgeBadge } from "@/components/KnowledgeBadge";
import {
  Eye, Megaphone, ShieldAlert, Users, RefreshCw, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";

/**
 * MUKTI 2.0 — Lead Room (/leads).
 *
 * Replaces "lots of raw ads" with a short prioritised list of **leads**.
 * No numeric 0-100 scores anywhere. Each row has:
 *   - Priority badge
 *   - Lead title + one-line "Why MUKTI surfaced this"
 *   - Source-diversity chips (Citizen / Classified / Police)
 *   - Change indicator (last_change_at)
 *   - Status pill (Unreviewed / Watching / Dismissed / Investigating)
 */
export default function LeadRoom() {
  const { user } = useAuth();
  const { t } = useLocale();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [filter, setFilter] = useState(""); // "", "high_attention", "review", "watch"

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?priority=${filter}` : "";
      const { data } = await api.get(`/leads${q}`);
      setLeads(data.leads || []);
    } catch (err) {
      toast.error("Could not load Lead Room");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const { data } = await api.post("/leads/rebuild");
      toast.success(
        `Rebuilt: ${data.built} lead(s) · ${data.high} high · ${data.review} review · ${data.watch} watch`,
      );
      fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div data-testid="lead-room-page" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-slate-900">{t("leads.title")}</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            {t("leads.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FilterPill current={filter} value="" label={t("leads.filter.all")} onClick={setFilter} />
          <FilterPill current={filter} value="high_attention" label={t("leads.filter.high")} onClick={setFilter} />
          <FilterPill current={filter} value="review" label={t("leads.filter.review")} onClick={setFilter} />
          <FilterPill current={filter} value="watch" label={t("leads.filter.watch")} onClick={setFilter} />
          {isAdmin ? (
            <Button
              variant="outline"
              size="sm"
              onClick={rebuild}
              disabled={rebuilding}
              data-testid="leads-rebuild-btn"
              className="gap-1.5 ml-2"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${rebuilding ? "animate-spin" : ""}`} />
              {rebuilding ? t("leads.rebuilding") : t("leads.rebuildBtn")}
            </Button>
          ) : null}
        </div>
      </header>

      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">{t("common.loading")}</div>
      ) : leads.length === 0 ? (
        <EmptyState onRebuild={isAdmin ? rebuild : undefined} />
      ) : (
        <div className="space-y-3" data-testid="lead-list">
          {leads.map((L) => <LeadRow key={L.id} lead={L} />)}
        </div>
      )}
    </div>
  );
}

function FilterPill({ current, value, label, onClick }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      data-testid={`lead-filter-${value || "all"}`}
      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );
}

function LeadRow({ lead }) {
  const breakdown = lead.signal_source_breakdown || {};
  return (
    <Link
      to={`/leads/${lead.id}`}
      data-testid={`lead-row-${lead.id}`}
      className="block border border-slate-200 bg-white rounded-lg p-4 hover:border-slate-300 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 pt-0.5">
          <PriorityBadge priority={lead.priority} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display font-semibold text-base text-slate-900 group-hover:text-slate-700 truncate">
              {lead.title}
            </h3>
            <KnowledgeBadge kind={lead.knowledge_type || "mukti_inference"} compact />
            <StatusChip status={lead.status} />
          </div>
          <p className="mt-1 text-sm text-slate-600 line-clamp-2">
            {lead.primary_reason}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
            <SourceChip icon={Users} label="Citizen" value={breakdown.citizen || 0} tone="emerald" />
            <SourceChip icon={Megaphone} label="Classified" value={breakdown.classified || 0} tone="sky" />
            <SourceChip icon={ShieldAlert} label="Police" value={breakdown.police || 0} tone="rose" />
            <span className="text-slate-400">·</span>
            <span className="font-mono text-slate-500">
              last change {lead.last_change_at ? lead.last_change_at.slice(0, 10) : "—"}
            </span>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-slate-700 shrink-0 mt-1" />
      </div>
    </Link>
  );
}

function SourceChip({ icon: Icon, label, value, tone }) {
  const toneClasses = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    sky: "bg-sky-50 text-sky-700 border-sky-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
  }[tone];
  const inactive = value === 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${
        inactive ? "bg-white text-slate-400 border-slate-100" : toneClasses
      }`}
    >
      <Icon className="h-3 w-3" />
      {label} <span className="font-mono">{value}</span>
    </span>
  );
}

function StatusChip({ status }) {
  if (!status || status === "unreviewed") return null;
  const map = {
    watching: { label: "WATCHING", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    investigating: { label: "INVESTIGATING", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    dismissed: { label: "DISMISSED", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  };
  const spec = map[status];
  if (!spec) return null;
  return (
    <span
      data-testid={`lead-status-${status}`}
      className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${spec.cls}`}
    >
      {spec.label}
    </span>
  );
}

function EmptyState({ onRebuild }) {
  return (
    <div
      data-testid="lead-room-empty"
      className="border border-dashed border-slate-300 rounded-lg bg-slate-50 py-16 text-center"
    >
      <Eye className="h-10 w-10 text-slate-300 mx-auto mb-3" />
      <h3 className="font-display text-lg font-semibold text-slate-700">Nothing demands attention yet</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
        MUKTI will surface leads here as advertisements, citizen reports and police intelligence accumulate.
      </p>
      {onRebuild ? (
        <Button onClick={onRebuild} variant="outline" size="sm" className="mt-4 gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" />
          Rebuild now
        </Button>
      ) : null}
    </div>
  );
}
